import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { DataTable, TablePagination } from "@/components/ui/data-table";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Modal from "@/components/ui/modal";
import ProductStockAdjustmentModal from "./components/ProductStockAdjustmentModal";
import ProductSerialBarcodeModal from "./components/ProductSerialBarcodeModal";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../Router/all_routes";
import {
  LayoutGrid,
  List as ListIcon,
  PlusCircle,
  Package,
  Edit,
  Trash2,
  Barcode,
  ScanBarcode,
  Copy,
  FileDown,
  FileUp,
  Filter,
  Image,
  MoreVertical,
  Search,
  Printer,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReactSelect } from "@/components/ui/react-select";
import { PageContainer } from "@/components/layout/PageContainer";

const ProductList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [categorySearchText, setCategorySearchText] = useState("");
  const [categoryLabels, setCategoryLabels] = useState({});
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'
  const [rowSelection, setRowSelection] = useState({});

  // New barcode handling state
  const [barcodeActionOpen, setBarcodeActionOpen] = useState(false);
  const [currentBarcodeUrl, setCurrentBarcodeUrl] = useState("");

  // Stock adjustment modal state
  const [selectedProductForStock, setSelectedProductForStock] = useState(null);
  const [stockAdjustmentModalOpen, setStockAdjustmentModalOpen] = useState(false);

  // Serial barcode modal state
  const [selectedProductForSerialBarcode, setSelectedProductForSerialBarcode] =
    useState(null);
  const [serialBarcodeModalOpen, setSerialBarcodeModalOpen] = useState(false);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [duplicateConfirmationOpen, setDuplicateConfirmationOpen] =
    useState(false);
  const [productToDuplicate, setProductToDuplicate] = useState(null);

  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("product_filters");
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
        category_id: "",
        brand_id: "",
        status: "",
        min_price: "",
        max_price: "",
        sku: "",
      };
  });

  // Reset page to 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, filters]);

  useEffect(() => {
    localStorage.setItem("product_filters", JSON.stringify(filters));
  }, [filters]);

  const [exportSettings, setExportSettings] = useState({
    format: "xlsx",
    category_id: "",
    store_id: "",
    status: "",
  });

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    return permissions && permissions.includes(perm);
  };

  // Queries
  const {
    data: productData,
    isLoading: isLoadingProducts,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ["products", currentPage, pageSize, debouncedSearchText, filters],
    queryFn: async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
          params: {
            page: currentPage,
            per_page: pageSize,
            search: debouncedSearchText || null,
            category_id: filters.category_id || null,
            brand_id: filters.brand_id || null,
            with: ["brand", "model", "unit", "categories"],
          },
        });
        return res.data;
      } catch (error) {
        toast.error(
          `Failed to load products: ${error.response?.data?.message || error.message}`,
          {
            action: {
              label: "Retry",
              onClick: () => refetchProducts(),
            },
          },
        );
        throw error;
      }
    },
  });

  const products = useMemo(() => productData?.data || [], [productData?.data]);
  const paginationMeta = {
    current_page:
      productData?.meta?.current_page || productData?.current_page || 1,
    last_page:
      productData?.meta?.last_page ||
      productData?.last_page ||
      Math.max(
        1,
        Math.ceil(
          (productData?.meta?.total || productData?.total || products.length) /
          pageSize,
        ),
      ),
    per_page: productData?.meta?.per_page || productData?.per_page || pageSize,
    total: productData?.meta?.total || productData?.total || products.length,
  };

  const { data: rawCategories = [] } = useQuery({
    queryKey: ["categories", "product-filter", categorySearchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: {
          perPage: 15,
          search: categorySearchText || undefined,
        },
      });
      return res.data?.data || res.data || [];
    },
  });

  const categories = useMemo(() => {
    if (Array.isArray(rawCategories)) return rawCategories;
    if (Array.isArray(rawCategories?.data)) return rawCategories.data;
    return [];
  }, [rawCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryLabels((prev) => {
        const next = { ...prev };
        categories.forEach((c) => {
          next[String(c.id)] = c.name;
        });
        return next;
      });
    }
  }, [categories]);
  useEffect(() => {
    if (products.length > 0) {
      const cLabels = {};
      products.forEach((p) => {
        if (Array.isArray(p.categories)) {
          p.categories.forEach((c) => {
            cLabels[String(c.id)] = c.name;
          });
        }
      });
      setCategoryLabels((prev) => ({ ...prev, ...cLabels }));
    }
  }, [products]);

  const { data: rawBrands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS);
      return res.data?.data || res.data || [];
    },
    enabled: hasPermission("brand read"),
  });

  const brands = useMemo(() => {
    if (Array.isArray(rawBrands)) return rawBrands;
    if (Array.isArray(rawBrands?.data)) return rawBrands.data;
    return [];
  }, [rawBrands]);

  // Mutations
  const exportMutation = useMutation({
    mutationFn: async (settings) => {
      const payload = {
        format: settings.format || null,
        category_id: settings.category_id || null,
        store_id: settings.store_id || null,
        status: settings.status === "" ? null : settings.status === "active",
      };

      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.EXPORT_PRODUCTS,
        payload,
      );
      return res.data;
    },
    onSuccess: (response) => {
      if (response.data && response.data.filename) {
        handleDownloadExport(response.data.filename);
      } else {
        toast.error(
          response.message || "Export initiated, but no filename received",
        );
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to initiate export");
    },
  });

  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiClient.post(
        API_ENDPOINTS.INVENTORY.IMPORT_PRODUCTS,
        formData,
        {
          timeout: 60000,
        },
      );
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      if (res.status === 207) {
        setImportErrors(res.data);
      } else {
        toast.success("Products imported successfully");
      }
      setShowImportModal(false);
      setImportFile(null);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (errorData && (errorData.error || errorData.message)) {
        setImportErrors({
          message: errorData.message || "Failed to import products",
          data: {
            failed: "All",
            failures: [
              {
                row: "N/A",
                attribute: "System Error",
                errors: [errorData.error || errorData.message],
              },
            ],
          },
        });
        setShowImportModal(false);
        setImportFile(null);
      } else {
        toast.error("Failed to import products");
      }
    },
  });

  const handleDownloadTemplate = async () => {
    try {
      toast.success("Downloading template...");
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.IMPORT_TEMPLATE, {
        responseType: "blob",
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        params: { _t: Date.now() },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const handleDownloadExport = async (filename) => {
    try {
      toast.success("Starting download...");
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.DOWNLOAD_EXPORT(filename),
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download complete");
      setShowExportModal(false);
    } catch {
      toast.error("Failed to download export file");
    }
  };

  const deleteProductMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product deleted successfully");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete product");
    },
  });

  const duplicateProductMutation = useMutation({
    mutationFn: async (id) => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.PRODUCT_DETAILS(id),
      );
      const productToDuplicate = res.data.data;
      const newProduct = {
        ...productToDuplicate,
        name: `Copy of ${productToDuplicate.name}`,
        sku: `${productToDuplicate.sku || ""}-copy`,
        barcode: `${productToDuplicate.barcode || ""}-copy`,
        status: "inactive",
        attributes: [],
      };
      delete newProduct.id;
      delete newProduct.created_at;
      delete newProduct.updated_at;

      const createRes = await apiClient.post(
        API_ENDPOINTS.INVENTORY.PRODUCTS,
        newProduct,
      );
      return createRes.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product duplicated successfully");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to duplicate product");
    },
  });

  useEffect(() => {
    const checkDarkMode = () => {
      const colorSchema = localStorage.getItem("colorschema");
      setIsDarkMode(colorSchema === "dark_mode");
    };

    checkDarkMode();
    window.addEventListener("storage", checkDarkMode);
    const interval = setInterval(checkDarkMode, 500);

    return () => {
      window.removeEventListener("storage", checkDarkMode);
      clearInterval(interval);
    };
  }, []);

  const filteredData = useMemo(() => {
    return products;
  }, [products]);

  const handleDelete = (id) => {
    setProductToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = () => {
    if (productToDelete) {
      deleteProductMutation.mutate(productToDelete);
      setDeleteConfirmationOpen(false);
      setProductToDelete(null);
    }
  };

  const handleDuplicate = (id) => {
    setProductToDuplicate(id);
    setDuplicateConfirmationOpen(true);
  };

  const handleDownloadBarcode = () => {
    if (!currentBarcodeUrl) return;
    const link = document.createElement("a");
    link.href = currentBarcodeUrl;
    link.download = `product_barcodes_${new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintBarcode = () => {
    if (!currentBarcodeUrl) return;

    // Create a hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = currentBarcodeUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      if (iframe.contentWindow) {
        // Small delay to ensure PDF content is ready for focus/print
        setTimeout(() => {
          iframe.contentWindow.focus();

          // Use the afterprint event to clean up the iframe after the user closes the print dialog
          iframe.contentWindow.onafterprint = () => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          };

          iframe.contentWindow.print();
        }, 200);
      }
    };
  };

  const handleGenerateBarcodes = async (productIds) => {
    if (!productIds || productIds.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    try {
      toast.loading("Generating barcodes PDF...", { id: "barcode-gen" });

      const params = new URLSearchParams();
      productIds.forEach((id) => params.append("product_ids[]", id));

      const res = await apiClient.get(API_ENDPOINTS.REPORTS.PRODUCT_BARCODE, {
        params,
        responseType: "blob",
      });

      // Revoke old URL if it exists
      if (currentBarcodeUrl) {
        window.URL.revokeObjectURL(currentBarcodeUrl);
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      setCurrentBarcodeUrl(url);
      setBarcodeActionOpen(true);

      toast.success("Barcodes generated successfully", { id: "barcode-gen" });
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to generate barcodes",
        { id: "barcode-gen" },
      );
    }
  };

  const confirmDuplicate = () => {
    if (productToDuplicate) {
      duplicateProductMutation.mutate(productToDuplicate);
      setDuplicateConfirmationOpen(false);
      setProductToDuplicate(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Product",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex items-center gap-2">
              {record.media?.[0]?.url ? (
                <img
                  src={record.media[0].url}
                  alt="Product"
                  className="h-10 w-10 rounded-md object-cover"
                />
              ) : (
                <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                  <Image className="h-5 w-5" />
                </div>
              )}
              <span className="font-medium text-foreground">{record.name}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "sku",
        header: "SKU",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "categories",
        header: "Category",
        cell: ({ row }) => {
          const categories = row.original.categories;
          return categories && categories.length > 0 ? categories[0].name : "-";
        },
      },
      ...(hasPermission("brand read")
        ? [
          {
            accessorKey: "brand",
            header: "Brand",
            cell: ({ row }) => {
              const brandName = row.original.brand?.name;
              const modelName = row.original.model?.name;
              return (
                <div>
                  <div>{brandName || "-"}</div>
                  {modelName && (
                    <div className="text-xs text-muted-foreground">
                      {modelName}
                    </div>
                  )}
                </div>
              );
            },
          },
        ]
        : []),
      ...(hasPermission("product view_cost_price")
        ? [
          {
            accessorKey: "cost",
            header: "Cost Price",
            cell: ({ getValue }) =>
              `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`,
          },
        ]
        : []),
      {
        accessorKey: "sell_price",
        header: "Sell Price",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge variant={status === "active" ? "success" : "destructive"}>
              {status === "active" ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {hasPermission("product update") && (
                  <DropdownMenuItem
                    onClick={() => {
                      navigate(
                        all_routes.editproduct.replace(":id", record.id),
                      );
                    }}
                  >
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {Boolean(record.manage_inventory) &&
                  record.tracking_type &&
                  record.tracking_type !== "none" &&
                  (hasPermission("stock adjustment create") ||
                    hasPermission("product update")) && (
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedProductForStock(record);
                        setStockAdjustmentModalOpen(true);
                      }}
                    >
                      <Package className="mr-2 h-4 w-4" /> Update Stock
                    </DropdownMenuItem>
                  )}
                {hasPermission("report serial barcode view") &&
                  Boolean(record.manage_inventory) &&
                  (record.tracking_type === "serial" ||
                    Boolean(record.is_serialized)) && (
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedProductForSerialBarcode(record);
                        setSerialBarcodeModalOpen(true);
                      }}
                    >
                      <ScanBarcode className="mr-2 h-4 w-4" /> Serial Barcode
                    </DropdownMenuItem>
                  )}
                {hasPermission("report product barcode view") && (
                  <DropdownMenuItem
                    onClick={() => handleGenerateBarcodes([record.id])}
                  >
                    <Barcode className="mr-2 h-4 w-4" /> Barcode
                  </DropdownMenuItem>
                )}
                {hasPermission("product create") && (
                  <DropdownMenuItem onClick={() => handleDuplicate(record.id)}>
                    <Copy className="mr-2 h-4 w-4" /> Duplicate
                  </DropdownMenuItem>
                )}
                {hasPermission("product delete") && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleDelete(record.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPermission, currencySymbol],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={<span className="flex items-center gap-2">Product List</span>}
          subtitle="Manage and track your inventory items"
        />
        <PageHeaderActions>
          <div className="flex items-center rounded-md border bg-background p-1">
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <ListIcon className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {hasPermission("product export") && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowExportModal(true)}
                title="Export"
              >
                <FileDown className="h-4 w-4" />
              </Button>
            )}
            {hasPermission("product import") && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowImportModal(true)}
                title="Import"
              >
                <FileUp className="h-4 w-4" />
              </Button>
            )}
          </div>

          {(hasPermission("report product barcode view") ||
            hasPermission("report serial barcode view")) &&
            Object.keys(rowSelection).length > 0 && (
              <div className="flex items-center gap-2">
                {hasPermission("report product barcode view") && (
                  <Button
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary/5 active:scale-95 transition-all"
                    onClick={() => handleGenerateBarcodes(Object.keys(rowSelection))}
                  >
                    <Barcode className="h-4 w-4" />
                    Generate Barcodes ({Object.keys(rowSelection).length})
                  </Button>
                )}
                {hasPermission("report serial barcode view") &&
                  (() => {
                    const selectedIds = Object.keys(rowSelection);
                    const selectedSerials = (filteredData || []).filter(
                      (p) =>
                        selectedIds.includes(String(p.id)) &&
                        Boolean(p.manage_inventory) &&
                        (p.tracking_type === "serial" || Boolean(p.is_serialized))
                    );
                    if (selectedSerials.length === 0) return null;
                    return (
                      <Button
                        variant="outline"
                        className="gap-2 border-primary/60 text-primary hover:bg-primary/5 active:scale-95 transition-all"
                        onClick={() => {
                          setSelectedProductForSerialBarcode(selectedSerials[0]);
                          setSerialBarcodeModalOpen(true);
                        }}
                        title="Generate Serial Barcode"
                      >
                        <ScanBarcode className="h-4 w-4" />
                        Serial Barcode
                        {selectedSerials.length > 1
                          ? ` (${selectedSerials.length})`
                          : ""}
                      </Button>
                    );
                  })()}
              </div>
            )}

          {hasPermission("report serial barcode view") && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                setSelectedProductForSerialBarcode(null);
                setSerialBarcodeModalOpen(true);
              }}
              title="Generate Serial Barcodes"
            >
              <ScanBarcode className="h-4 w-4" />
              Serial Barcodes
            </Button>
          )}

          {hasPermission("product create") && (
            <Link to={all_routes.openingStock}>
              <Button variant="outline" className="gap-2">
                <Package className="h-4 w-4" />
                Opening Stock
              </Button>
            </Link>
          )}

          {hasPermission("product create") && (
            <Link to={all_routes.addproduct}>
              <Button variant="default" className="gap-2">
                <PlusCircle className="h-4 w-4" />
                Add Product
              </Button>
            </Link>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search by name, SKU, brand, model..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <FilterActions>
          <Button
            variant="outline"
            onClick={() => setShowFilterModal(true)}
            className="flex-1 md:flex-none"
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
            {Object.values(filters).some(Boolean) && (
              <Badge variant="secondary" className="ml-2">
                {Object.values(filters).filter(Boolean).length}
              </Badge>
            )}
          </Button>
          {Object.values(filters).some(Boolean) && (
            <Button
              variant="ghost"
              onClick={() =>
                setFilters({
                  category_id: "",
                  brand_id: "",
                  status: "",
                  min_price: "",
                  max_price: "",
                  sku: "",
                })
              }
              className="text-muted-foreground"
            >
              Clear
            </Button>
          )}
        </FilterActions>
      </FilterCard>

      <TableCard>
        {isLoadingProducts ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner size={32} />
          </div>
        ) : viewMode === "list" ? (
          <DataTable
            columns={columns}
            data={filteredData}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(row) => row.id}
            totalRows={paginationMeta.total}
            itemLabel="products"
            pageSizeOptions={[10, 15, 25, 50, 100]}
            pagination={{
              pageIndex: currentPage - 1,
              pageSize: pageSize,
            }}
            pageCount={paginationMeta.last_page}
            onPaginationChange={(updater) => {
              const oldState = {
                pageIndex: currentPage - 1,
                pageSize,
              };
              const newState =
                typeof updater === "function" ? updater(oldState) : updater;
              if (
                newState.pageSize !== undefined &&
                newState.pageSize !== pageSize
              ) {
                setPageSize(newState.pageSize);
                setCurrentPage(1);
              } else if (newState.pageIndex !== undefined) {
                setCurrentPage(newState.pageIndex + 1);
              }
            }}
            onRowClick={(row) => {
              navigate(all_routes.editproduct.replace(":id", row.id));
            }}
          />
        ) : (
          <>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[2000px]:grid-cols-7 gap-6">
                {filteredData.map((product) => (
                  <Card
                    key={product.id}
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => {
                      navigate(
                        all_routes.editproduct.replace(":id", product.id),
                      );
                    }}
                  >
                    <div className="aspect-square relative bg-muted/20">
                      {product.media?.[0]?.url ? (
                        <img
                          src={product.media[0].url}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                          <Image className="h-16 w-16" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge
                          variant={
                            product.status === "active"
                              ? "success"
                              : "destructive"
                          }
                        >
                          {product.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3
                            className="font-semibold text-lg line-clamp-1"
                            title={product.name}
                          >
                            {product.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            SKU: {product.sku || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-4">
                        <div className="font-bold text-lg text-primary">
                          {currencySymbol}
                          {parseFloat(product.sell_price).toFixed(2)}
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {hasPermission("product update") && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    navigate(
                                      all_routes.editproduct.replace(
                                        ":id",
                                        product.id,
                                      ),
                                    );
                                  }}
                                >
                                  <Edit className="mr-2 h-4 w-4" /> Edit
                                </DropdownMenuItem>
                              )}
                              {Boolean(product.manage_inventory) &&
                                product.tracking_type &&
                                product.tracking_type !== "none" &&
                                (hasPermission("stock adjustment create") ||
                                  hasPermission("product update")) && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedProductForStock(product);
                                      setStockAdjustmentModalOpen(true);
                                    }}
                                  >
                                    <Package className="mr-2 h-4 w-4" /> Update Stock
                                  </DropdownMenuItem>
                                )}
                              {hasPermission("report serial barcode view") &&
                                Boolean(product.manage_inventory) &&
                                (product.tracking_type === "serial" ||
                                  Boolean(product.is_serialized)) && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedProductForSerialBarcode(
                                        product
                                      );
                                      setSerialBarcodeModalOpen(true);
                                    }}
                                  >
                                    <ScanBarcode className="mr-2 h-4 w-4" /> Serial Barcode
                                  </DropdownMenuItem>
                                )}
                              {hasPermission("report product barcode view") && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleGenerateBarcodes([product.id])
                                  }
                                >
                                  <Barcode className="mr-2 h-4 w-4" /> Barcode
                                </DropdownMenuItem>
                              )}
                              {hasPermission("product create") && (
                                <DropdownMenuItem
                                  onClick={() => handleDuplicate(product.id)}
                                >
                                  <Copy className="mr-2 h-4 w-4" /> Duplicate
                                </DropdownMenuItem>
                              )}
                              {hasPermission("product delete") && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => handleDelete(product.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            {/* Pagination for Grid View */}
            <TablePagination
              currentPage={currentPage}
              totalPages={paginationMeta.last_page}
              pageSize={pageSize}
              totalItems={paginationMeta.total}
              itemLabel="products"
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 15, 25, 50, 100]}
              showPageSize={true}
              showQuickJumper={true}
              className="border-t"
            />
          </>
        )}
      </TableCard>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Filter Products</DialogTitle>
            <DialogDescription>
              Narrow down your product list by category, brand, status, and
              price.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Category</Label>
              <ReactSelect
                options={[
                  { value: "", label: "All Categories" },
                  ...(Array.isArray(categories) ? categories : []).map((c) => ({
                    value: String(c.id),
                    label: c.name,
                  })),
                ]}
                onInputChange={(val, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    setCategorySearchText(val);
                  }
                }}
                value={
                  filters.category_id
                    ? {
                      value: filters.category_id,
                      label:
                        categoryLabels[String(filters.category_id)] ||
                        (Array.isArray(categories) ? categories : []).find(
                          (c) => String(c.id) === String(filters.category_id),
                        )?.name ||
                        "Selected Category",
                    }
                    : { value: "", label: "All Categories" }
                }
                onChange={(opt) =>
                  setFilters({ ...filters, category_id: opt ? opt.value : "" })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Brand</Label>
              <ReactSelect
                options={[
                  { value: "", label: "All Brands" },
                  ...(Array.isArray(brands) ? brands : []).map((b) => ({
                    value: String(b.id),
                    label: b.name,
                  })),
                ]}
                value={
                  filters.brand_id
                    ? {
                      value: filters.brand_id,
                      label:
                        (Array.isArray(brands) ? brands : []).find(
                          (b) => String(b.id) === String(filters.brand_id),
                        )?.name || "All Brands",
                    }
                    : { value: "", label: "All Brands" }
                }
                onChange={(opt) =>
                  setFilters({ ...filters, brand_id: opt ? opt.value : "" })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <ReactSelect
                options={[
                  { value: "", label: "All Statuses" },
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={
                  filters.status === ""
                    ? { value: "", label: "All Statuses" }
                    : [
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ].find((opt) => opt.value === filters.status)
                }
                onChange={(opt) =>
                  setFilters({ ...filters, status: opt ? opt.value : "" })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Price Range</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.min_price}
                  onChange={(e) =>
                    setFilters({ ...filters, min_price: e.target.value })
                  }
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.max_price}
                  onChange={(e) =>
                    setFilters({ ...filters, max_price: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <Button variant="default" onClick={() => setShowFilterModal(false)}>
            Apply Filters
          </Button>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={deleteConfirmationOpen}
        onOpenChange={setDeleteConfirmationOpen}
      >
        <AlertDialogContent
          className={`max-w-[380px] shadow-2xl border transition-colors ${isDarkMode ? "bg-slate-950 text-white" : "bg-white text-slate-950"
            }`}
        >
          <AlertDialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-destructive/10">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>Delete Product</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete this product? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground transition-all active:scale-95"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Confirmation Modal */}
      <AlertDialog
        open={duplicateConfirmationOpen}
        onOpenChange={setDuplicateConfirmationOpen}
      >
        <AlertDialogContent
          className={`max-w-[380px] shadow-2xl border transition-colors ${isDarkMode ? "bg-slate-950 text-white" : "bg-white text-slate-950"
            }`}
        >
          <AlertDialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-primary/10">
                <Copy className="h-5 w-5 text-primary" />
              </div>
              <AlertDialogTitle>Duplicate Product</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to duplicate this product? A copy will be
              created.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDuplicate}
              className="bg-primary hover:bg-primary/90 text-primary-foreground transition-all active:scale-95"
            >
              Duplicate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Products</DialogTitle>
            <DialogDescription>
              Upload a bulk product file to add multiple products at once.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a bulk product file to add multiple products at once.
              Supported formats: .xlsx, .csv. Download the template to ensure
              your file matches the required format.
            </p>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <FileDown className="mr-2 h-4 w-4" /> Download Template
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Upload File (XLSX, CSV)</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => importMutation.mutate(importFile)}
              disabled={!importFile || importMutation.isPending}
            >
              {importMutation.isPending ? "Importing..." : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Products</DialogTitle>
            <DialogDescription>
              Download your product list in your preferred format.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Download your product list in your preferred format. Select
              options below to customize your export.
            </p>
            <div className="grid gap-2">
              <Label>Format</Label>
              <ReactSelect
                options={[
                  { value: "xlsx", label: "Excel (XLSX)" },
                  { value: "csv", label: "CSV" },
                  { value: "pdf", label: "PDF" },
                ]}
                value={[
                  { value: "xlsx", label: "Excel (XLSX)" },
                  { value: "csv", label: "CSV" },
                  { value: "pdf", label: "PDF" },
                ].find((opt) => opt.value === exportSettings.format)}
                onChange={(opt) =>
                  setExportSettings({
                    ...exportSettings,
                    format: opt ? opt.value : "xlsx",
                  })
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => exportMutation.mutate(exportSettings)}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? "Exporting..." : "Export"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Barcode Action Modal */}
      <Dialog open={barcodeActionOpen} onOpenChange={setBarcodeActionOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Barcodes Generated</DialogTitle>
            <DialogDescription>
              Your barcodes are ready. Would you like to download the PDF or
              print it directly?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-3 justify-center py-6">
            <Button
              className="flex-1 gap-2"
              variant="outline"
              onClick={handleDownloadBarcode}
            >
              <FileDown className="h-4 w-4" /> Download PDF
            </Button>
            <Button className="flex-1 gap-2" onClick={handlePrintBarcode}>
              <Printer className="h-4 w-4" /> Print Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!importErrors}
        onOpenChange={(open) => !open && setImportErrors(null)}
      >
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Import Completed with Errors
            </DialogTitle>
            <DialogDescription>
              {importErrors?.message || "Some rows failed to import."} (Failed
              rows: {importErrors?.data?.failed})
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 py-4">
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="h-10 px-4 text-left font-medium">Row</th>
                    <th className="h-10 px-4 text-left font-medium">
                      Attribute
                    </th>
                    <th className="h-10 px-4 text-left font-medium">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors?.data?.failures?.map((f, i) => (
                    <tr
                      key={i}
                      className="border-t hover:bg-muted/50 transition-colors"
                    >
                      <td className="p-4 align-top font-medium">{f.row}</td>
                      <td className="p-4 align-top">{f.attribute || "-"}</td>
                      <td className="p-4 align-top text-destructive">
                        <ul className="list-disc pl-4 space-y-1">
                          {f.errors?.map((err, j) => (
                            <li key={j}>{err}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end border-t pt-4">
            <Button variant="outline" onClick={() => setImportErrors(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Modal */}
      <ProductStockAdjustmentModal
        open={stockAdjustmentModalOpen}
        onOpenChange={setStockAdjustmentModalOpen}
        product={selectedProductForStock}
        onSuccess={() => {
          refetchProducts();
        }}
      />

      {/* Serial Barcode Modal */}
      <ProductSerialBarcodeModal
        open={serialBarcodeModalOpen}
        onOpenChange={setSerialBarcodeModalOpen}
        product={selectedProductForSerialBarcode}
      />
    </PageContainer>
  );
};

export default ProductList;
